import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ReferenceDot,
} from 'recharts';
import { format, parseISO, addDays } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface BreakEvenDataPoint {
    date: string;
    cumulative?: number;
    projected?: number;
    isBreakEven?: boolean;
}

export const BreakEvenProjectionChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const { chartData, breakEvenPoint } = useMemo(() => {
        if (!finance?.dailyBreakdown) return { chartData: [], breakEvenPoint: null as BreakEvenDataPoint | null };

        const sorted = Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                return { date, net: metrics?.net ?? 0 };
            });

        if (sorted.length === 0) return { chartData: [], breakEvenPoint: null as BreakEvenDataPoint | null };

        // Build cumulative profit series
        let cumulative = 0;
        const result: BreakEvenDataPoint[] = sorted.map(d => {
            cumulative += d.net;
            return { date: d.date, cumulative };
        });

        let bePoint: BreakEvenDataPoint | null = null;

        // Check if break-even already happened within the actual data
        for (let i = 1; i < result.length; i++) {
            const prev = result[i - 1].cumulative ?? 0;
            const curr = result[i].cumulative ?? 0;
            if (prev < 0 && curr >= 0) {
                bePoint = { date: result[i].date, cumulative: 0, isBreakEven: true };
                break;
            }
        }

        // If cumulative is still negative at the end, project forward
        if (!bePoint && cumulative < 0) {
            const avgDailyNet = sorted.reduce((sum, d) => sum + d.net, 0) / sorted.length;

            if (avgDailyNet > 0) {
                const lastDate = parseISO(sorted[sorted.length - 1].date);
                const daysToBreakEven = Math.ceil(Math.abs(cumulative) / avgDailyNet);
                const maxProjectionDays = Math.min(daysToBreakEven + 5, 90);

                // Connect actual to projected: add projected value to last actual point
                const lastIdx = result.length - 1;
                result[lastIdx].projected = result[lastIdx].cumulative;

                let projCumulative = cumulative;
                for (let k = 1; k <= maxProjectionDays; k++) {
                    projCumulative += avgDailyNet;
                    const projDate = format(addDays(lastDate, k), 'yyyy-MM-dd');
                    const point: BreakEvenDataPoint = {
                        date: projDate,
                        projected: projCumulative,
                    };

                    if (!bePoint && projCumulative >= 0) {
                        point.isBreakEven = true;
                        bePoint = { date: projDate, projected: projCumulative, isBreakEven: true };
                    }

                    result.push(point);
                }
            }
        }

        return { chartData: result, breakEvenPoint: bePoint };
    }, [finance?.dailyBreakdown]);

    const finalCumulative = useMemo(() => {
        if (chartData.length === 0) return 0;
        const last = chartData[chartData.length - 1];
        return last.projected ?? last.cumulative ?? 0;
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: finalCumulative >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense }}>
                {breakEvenPoint
                    ? t('charts.breakEvenOn', 'Break-even: {{date}}', { date: format(parseISO(breakEvenPoint.date), 'MMM d') })
                    : formatCompactNumber(finalCumulative, currencySymbol)
                }
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <defs>
                            <linearGradient id="breakEvenPositive" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.revenue} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={CHART_COLORS.revenue} stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="breakEvenNegative" x1="0" y1="1" x2="0" y2="0">
                                <stop offset="5%" stopColor={CHART_COLORS.expense} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={CHART_COLORS.expense} stopOpacity={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => {
                                const label = name === 'cumulative'
                                    ? t('charts.cumulativeProfit', 'Cumulative Profit')
                                    : t('charts.projectedProfit', 'Projected Profit');
                                return [formatCurrency(value, currencySymbol), label];
                            }}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                        <Area
                            type="monotone"
                            dataKey="cumulative"
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            fill="url(#breakEvenPositive)"
                            dot={false}
                            activeDot={CHART_STYLES.line.activeDot}
                            name="cumulative"
                            connectNulls={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="projected"
                            stroke={CHART_COLORS.profit}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            strokeDasharray="6 4"
                            strokeOpacity={0.7}
                            dot={false}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.profit }}
                            name="projected"
                            connectNulls={false}
                        />
                        {breakEvenPoint && (
                            <ReferenceDot
                                x={breakEvenPoint.date}
                                y={0}
                                r={6}
                                fill={CHART_COLORS.accent}
                                stroke="var(--bg-elevated)"
                                strokeWidth={2}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
